<template>
  <div class="mt-6 px-3">
    <div class="flex-grow grid grid-cols-1 lg:grid-cols-2 gap-x-8">
      <div>
        <div class="flex justify-between items-center pb-7 border-b">
          <div class="pl-2.5">
            <p class="text-base font-semibold font-poppins text-gray-700">Require Submission</p>
            <p class="text-gray-500">
              Students must submit a unique response before they can see and
              comment on their classmate's ideas.
            </p>
          </div>
          <toggle-button
            :disabled="is_saving"
            :labels="false"
            v-model="require_submission"
            color="#72ccd7"
            class="toggle-button-class"
          />
        </div>
        <div class="py-7 border-b">
          <div class="flex justify-between items-center">
            <div class="pl-2.5">
              <div>
                <span class="text-base font-semibold font-poppins text-gray-700">Anonymity</span>
                <button class="px-2 text-gray-600 hover:text-gray-700" v-popover:anonymity-info.right>
                  <i class="fas fa-info-circle "></i>
                </button>
              </div>
              <p class="text-gray-500">
                Students will be anonymous to each other with pseudonymous of
                accomplished people from history.
              </p>
            </div>
            <toggle-button
              :disabled="is_saving"
              :labels="false"
              v-model="anonymity_status"
              color="#72ccd7"
              class="toggle-button-class"
            />
          </div>
          <popover
            class=" bg-white drop-shadow absolute z-auto"
            name="anonymity-info"
            :width="380"
          >
            <div class="px-4 py-3">
              Students will be randomly <b class="font-semibold text-gray-700">assigned a different identity</b> for each
              discussion. The teacher will never be anonymous in the discussion.
            </div>
          </popover>
        </div>
        <div class="py-7 border-b">
          <div class="flex justify-between items-center">
            <div class="pl-2.5">
              <div>
                <span class="text-base font-semibold font-poppins text-gray-700">Close Date</span>
                <button class="px-2 text-gray-600 hover:text-gray-700" v-popover:close-date-info.right>
                  <i class="fas fa-info-circle "></i>
                </button>
              </div>
              <p class="text-gray-500">
                Set a date and time to automatically close the RoundTable.
              </p>
            </div>
            <toggle-button
              :disabled="is_saving"
              :sync="true"
              :labels="false"
              v-model="close_date.enabled"
              color="#72ccd7"
              class="toggle-button-class"
            />
          </div>
          <popover
            class=" bg-white drop-shadow absolute z-auto"
            name="close-date-info"
            :width="350"
          >
            <div class="px-4 py-3 text-gray-700">
              This feature will <b class="font-semibold">automatically close the RoundTable</b> at the date
              and time provided. It will also send an email reminder to students
              24 hours before closing time.
            </div>
          </popover>
          <div
            v-if="close_date.enabled"
            class="mt-4 pl-2.5 flex flex-col sm:flex-row"
          >
            <div class="bg-gray-100 rounded px-5 py-2 flex items-center">
              <DatePicker
                class="cursor-pointer bg-gray-100 w-full relative"
                v-model="close_date.date"
                placeholder="Select a date"
                :config="config"
                :disabled="is_saving"
              >
              </DatePicker>
              <a class="cursor-pointer" data-toggle>
                <i class="fas fa-calendar-alt text-gray-500"></i>
              </a>
            </div>
            <div class="flex items-center mt-2 sm:mt-0 sm:ml-6">
              <input
                :disabled="is_saving"
                v-model="hard_close_enable"
                type="checkbox"
                class="mr-2.5 cursor-pointer"
              />
              <div class="text-xs text-gray-500">
                <p class="font-semibold">Hard Close</p>
                <p>Disable contributions after close date.</p>
              </div>
            </div>
          </div>
        </div>
        <!-- Configuration Email ---------------------------------------------------------------------- -->
        <div class="py-7 border-b">
          <div class="flex justify-between items-center">
            <div class="pl-2.5">
              <div>
                <span class="text-base font-semibold font-poppins text-gray-700">Email Notifications</span>
                <button class="px-2 text-gray-600 hover:text-gray-700" v-popover:email-info.right>
                  <i class="fas fa-info-circle "></i>
                </button>
              </div>
              <p class="text-gray-500">
                Participants can receive an email notification for comments and
                replies.
              </p>
            </div>
            <toggle-button
              :disabled="is_saving"
              :sync="true"
              :labels="false"
              v-model="is_email_notifications_enable"
              color="#72ccd7"
              class="toggle-button-class"
            />
          </div>
          <popover
            class="bg-white drop-shadow absolute z-auto"
            name="email-info"
            :width="420"
          >
            <div class="px-4 py-3 text-gray-700">
              With email notifications turned on, participants will receive an
              email <b class="font-semibold">when someone</b> comments on their submission or replies to
              their comment. They will not receive email notifications if they
              are logged into Parlay.
            </div>
          </popover>
        </div>
        <!-- Genie Feedback -->
        <div class="py-7 border-b">
          <div class="flex justify-between items-center">
            <div class="pl-2.5">
              <div>
                <span class="text-base font-semibold font-poppins text-gray-700">Genie Feedback</span>
                <button class="px-2 text-gray-600 hover:text-gray-700" v-popover:feedback-info.right>
                  <i class="fas fa-info-circle "></i>
                </button>
              </div>
              <p class="text-gray-500">
                Participants can use A.I. to help them improving submissions
                based on RoundTable Assessment Criterias.
              </p>
            </div>
            <toggle-button
              :disabled="is_saving"
              :sync="true"
              :labels="false"
              v-model="is_genie_feedback_enable"
              color="#72ccd7"
              class="toggle-button-class"
            />
          </div>
          <popover
            class=" bg-white drop-shadow absolute z-auto"
            name="feedback-info"
            :width="480"
          >
            <div class="px-4 py-3 text-gray-700">
              Enabling Genie Feedback provides participants with the choice to <b class="font-semibold">receive instant feedback</b> on their submissions, 
              which is tailored to the criteria <b class="font-semibold">set by the instructor </b>for the RoundTable. 
              If no specific criteria are set, participants will receive general feedback. 
              The instructor <b class="font-semibold">can view this feedback</b> as well as any revisions made by the participant in response to the feedback.
            </div>
          </popover>
        </div>
        <div class="py-7">
          <div class="flex justify-between items-center">
            <div class="pl-2.5">
              <div>
                <span class="text-base font-semibold font-poppins text-gray-700">Assessment</span>
                <button class="px-2 text-gray-600 hover:text-gray-700" v-popover:assessment-info.right>
                  <i class="fas fa-info-circle "></i>
                </button>
              </div>
              <p class="text-gray-500">
                Teacher can assess students based on predefined criteria.
              </p>
            </div>
            <toggle-button
              :disabled="is_saving"
              :sync="true"
              :labels="false"
              v-model="assessment_status"
              color="#72ccd7"
              class="toggle-button-class"
            />
          </div>
          <popover
            class="bg-white drop-shadow absolute z-auto"
            name="assessment-info"
            :width="400"
          >
            <div class="px-4 py-3 text-gray-700">
              Create <b class="font-semibold">your own criteria</b> or use <b class="font-semibold">one of the defaults</b>. Set a Max
              Score for a rubric-style assessment, or leave it off for a
              tally-based approach.
            </div>
          </popover>
          <div v-if="assessment_status" class="mt-4 pl-2.5">
            <div class="flex flex-col sm:flex-row">
              <div
                class="bg-gray-100 border border-solid border-gray-200 text-gray-500 rounded-full p-0.5 max-w-xxs"
              >
                <button
                  class=" px-3.5 py-1 rounded-full font-poppins text-gray-700"
                  :class="max_score_enabled === false ? 'bg-white border border-solid border-gray-200' : ''"
                  @click="max_score_enabled = !max_score_enabled"
                >
                  Tally
                </button>
                <button
                  class="px-3.5 py-1 rounded-full font-poppins text-gray-700"
                  :class="max_score_enabled === true ? 'bg-white border border-solid border-gray-200' : ''"
                  @click="max_score_enabled = !max_score_enabled"
                >
                  Rubric
                </button>
              </div>
              <div v-if="max_score_enabled" class="flex items-center mt-1 sm:mt-0 sm:ml-5">
                <div>
                  <p class="text-sm font-semibold font-poppins text-gray-700">Max Score</p>
                  <p class="text-gray-600 text-xs font-poppins">For each criteria</p>
                </div>
                <div class="ml-5 flex items-center space-x-1">
                  <button class="sample text-gray-500 text-xs hover:bg-gray-100 rounded-full h-7 w-7" @click="point_scale -= 1" :disabled="Number(point_scale) === 0">
                    <i class="fa fa-chevron-left"></i>
                  </button>
                  <input
                    :disabled="is_saving"
                    @input="inputed"
                    v-model="point_scale"
                    class="w-10 h-7 rounded text-center custom-border"
                  />
                  <button class="sample text-gray-500 text-xs hover:bg-gray-100 rounded-full h-7 w-7" @click="addPoint">
                    <i class="fa fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>
            <div
              class="mt-3"
              v-for="(point_type, index) in getCourseDetail.points_settings.peer_rewards"
              :key="index"
            >
              
              <div class="flex justify-between border border-solid border-gray-200 rounded px-4 space-x-2 py-2.5">
                <div class="w-full flex items-center space-x-3">
                  <Popper 
                    :append-to-body="true"
                    :prevent-default="true"
                    trigger="clickToToggle"
                    :options="{
                      placement: 'right',
                      modifiers: { offset: { offset: '0,8px' } }
                    }"
                    @show="openPopper"
                    @hide="closePopper"
                  >
                    <div class="popper bg-white rounded-md shadow-sm relative w-64 pb-4" v-click-outside="closePopper">
                      <div v-if="point_type.title" class="py-2.5 font-semibold text-sm font-poppins text-left w-full px-3">
                        {{ point_type.title }}
                      </div>
                      <div v-if="point_type.description"class="text-left text-xs flex flex-col px-3 text-gray-700 font-poppins font-light">
                        <div class="html-content font-poppins font-light" v-html="typeof point_type.description === 'object' ? point_type.description.join('. ') : point_type.description"></div>
                      </div>
                      <div v-if="max_score_enabled === true" class="mt-4 px-3">
                        <div v-if="point_type.points_description && point_type.points_description.length > 0">
                          <div v-for="(point) in point_type.points_description" class="flex flex-col text-left text-xs space-y-2">
                            <div v-if="point.description !== ''" class="flex items-center space-x-1 mb-2">
                              <div class=""><span class="font-semibold font-poppins text-gray-800">({{ point.value }}) Point: </span><span class="text-gray-700">{{ point.description }}</span></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <button type="button"  class="h-full items-center font-semibold hover:underline font-poppins text-gray-600 hover:text-gray-700" slot="reference">
                      <i class="fa-solid fa-circle-info"></i>
                    </button>
                  </Popper>
                  <button @click="handleEditAssessmentPoint(point_type)" class="text-sm cursor-pointer text-gray-700 hover:underline">{{ point_type.title }}</button>
                </div>
                <div class="flex items-center space-x-2">
                  <input
                    v-model="selected_points"
                    :value="point_type.tag"
                    type="checkbox"
                    class="cursor-pointer"
                  />
                </div>
              </div>
            </div>
            <div class="mt-3 flex items-center space-x-2">
              <div class="w-full">
                <input
                  class="px-3 py-2 w-full border rounded border-solid border-gray-200 placeholder-gray-400 bg-white"
                  type="text"
                  v-model="point_title"
                  :placeholder="'Enter a new criteria title...'"
                  @keyup.enter="handleCreatePoint()"
                />
              </div>
              <button
                @click="handleCreatePoint()"
                class="bg-blueGray-700 rounded text-white px-3 py-1.5 border border-solid border-blueGray-700 disabled:opacity-50"
                :disabled="point_title === '' ? true : false"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      </div>
      <div>
        <div class="mt-3 md:mt-0">
          <span class="text-base font-semibold font-poppins text-gray-700">Peer Feedback Questions & Sentence Stems</span>
          <button class="px-2" v-popover:peer-feedback-info.right>
            <i class="fas fa-info-circle text-gray-600"></i>
          </button>
        </div>
        <p class="text-gray-500">
          Students will see these peer feedback questions when they respond to
          their classmates.
        </p>
        <popover
          class=" bg-white drop-shadow absolute z-auto"
          name="peer-feedback-info"
          :width="350"
        >
          <div class="px-4 py-3 text-gray-700">
            The students can click on the <b class="font-semibold">question to auto-populate</b> it in their comment box.
          </div>
        </popover>
        <div
          v-for="(q, index) in peer_feedback_questions"
          :key="index"
          class="mt-4 flex justify-between"
        >
          <span class="w-10/12 text-sm">{{ index + 1 }}. {{ q.question }}</span>
          <div class="flex items-center">
            <i
              v-if="q.is_custom"
              :disabled="is_saving"
              class="fas fa-trash-alt mr-1.5 text-red-200 hover:text-red-700 cursor-pointer"
              @click="deletePeerQuestion(index)"
            ></i>
            <i
              :disabled="is_saving"
              class="fas fa-edit mr-1.5 text-gray-200 hover:text-gray-500 cursor-pointer"
              @click="editPeerQuestion(index)"
            ></i>
            <input
              :disabled="is_saving"
              v-model="q.is_active"
              type="checkbox"
              class="cursor-pointer"
            />
          </div>
        </div>
        <div class="mt-5 flex items-center">
          <input
            class="px-4 py-2 w-full bg-input rounded placeholder-gray-300"
            type="text"
            v-model="peer_question_new"
            :placeholder="'New Question'"
            :disabled="is_saving"
            @keyup.enter="addPeerQuestion"
          />
          <i
            :disabled="is_saving"
            @click="addPeerQuestion"
            class="sample fas fa-plus-circle text-2xl ml-3 text-gray-400 cursor-pointer"
          ></i>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import Vue from 'vue';
  import { mapGetters } from 'vuex';
  import { filter, uniqBy, find } from 'lodash';
  import { EventBus } from 'utils/event-bus';
  import onlineRoundtableSockets from 'mixins/sockets/onlineRoundtableSocketEvents';
  import courseSocketMixin from 'mixins/sockets/courseSocketEvents';
  import moment from 'moment';
  import Popper from 'vue-popperjs';
  export default Vue.extend({
    components: {
      DatePicker: () => import('vue-flatpickr-component'),
      Popper
    },
    data() {
      return {
        is_saving: false,
        require_submission: true,
        anonymity_status: true,
        hard_close_date: false,
        close_date: {
          enabled: false,
          date: null,
        },
        hard_close_enable: false,
        assessment_status: false,
        max_score_enabled: false,
        point_scale: 0,
        selected_points: [],
        point_title: null,
        point_description: null,
        points_rubric_description: [],
        point_title_error_message: '',
        point_description_error_message: '',
        peer_feedback_questions: [],
        peer_question_new: '',
        config: {
          altFormat: 'F j, G:i K',
          altInput: true,
          enableTime: true,
          wrap: true,
        },
        is_email_notifications_enable: false,
        is_genie_feedback_enable: false,
        popperName: '',
        info_open: false,
      };
    },
    watch: {
      handleChangeSettings: function () {
        this.$emit('handleSettingsChange', this.handleChangeSettings);
      },
      'close_date.enabled': function (newValue) {
        if (newValue === true && this.getCurrentRT.end_date === null) {
          this.close_date.date = new Date();
          return;
        }
        if (newValue === false && this.getCurrentRT.end_date !== null) {
          this.hard_close_enable = false;
          this.close_date.date = new Date();
          return;
        }
        this.close_date.date = moment(this.getCurrentRT.end_date).format();
        return;
      },
    },
    computed: {
      ...mapGetters([
        'getCurrentRT',
        'getCourseDetail',
        'hasTeacherPermissionsInCourse',
        'getProfile',
      ]),
      full_point_settings() {
        return this.selected_points.map((tag) =>
          find(this.available_point_types, (point) => point.tag === tag)
        );
      },
      handleChangeSettings: function () {
        const properties = {
          require_submission:
            this.require_submission !== this.getCurrentRT.require_submission
              ? true
              : false,
          anonymity_status:
            this.anonymity_status !== this.getCurrentRT.enable_annonymous
              ? true
              : false,
          hard_close:
            this.hard_close_enable !== this.getCurrentRT.hard_close &&
            this.close_date.enabled != this.getCurrentRT.is_roundtable_end
              ? true
              : false,
          close_enable:
            moment(this.close_date.date).format('LLL') !==
            moment(this.getCurrentRT.end_date).format('LLL')
              ? true
              : false,
          close_date_value:
            moment(this.close_date.date).format() ===
              moment(this.getCurrentRT.end_date).format() ||
            this.getCurrentRT.end_date === null
              ? false
              : true,
          assessment_status:
            this.assessment_status !== this.getCurrentRT.enable_grading
              ? true
              : false,
          max_score_enabled:
            this.is_max_score_enable === this.max_score_enabled ? false : true,
          point_scale: this.max_score_value !== this.point_scale ? true : false,
          peer_feedback_questions:
            this.arraysAreEqual(
              this.tags_selected_peer_feed_questions_new,
              this.tags_selected_peer_feed_questions_current
            ) === true
              ? false
              : true,
          selected_points:
            this.arePointsExist(
              this.tags_selected_points_current,
              this.selected_points
            ) === true
              ? false
              : true,
          is_email_notifications_enable:
            this.is_email_notifications_enable !==
            this.getCurrentRT.is_email_notifications
              ? true
              : false,
          is_genie_feedback_enable:
            this.is_genie_feedback_enable !==
            this.getCurrentRT.is_genie_feedback_enable
              ? true
              : false,
        };
        const areAllFalse = Object.values(properties).every(
          (value) => value === false
        );
        return areAllFalse;
      },
      available_point_types() {
        const coursePoints = filter(this.getCourseDetail.points_settings.peer_rewards, (reward) => {
          const { tag } = reward;
          const prefix = 'rt';
          return (
            tag.substring(0, 2) === prefix || tag.substring(0, 3) === 'all'
          );
        });
        const points = uniqBy([...coursePoints, ...this.getCurrentRT.points_settings.peer_rewards],'tag').filter((point) => point);
        return points;
      },
      error_present() {
        return (
          this.point_description_error_message || this.point_title_error_message
        );
      },
      is_max_score_enable: function () {
        let is_max_score_enable = false;
        this.getCurrentRT.point_cap === null
          ? (is_max_score_enable = false)
          : (is_max_score_enable = true);
        return is_max_score_enable;
      },
      max_score_value: function () {
        let max_score_current_value;
        if (this.getCurrentRT.point_cap !== null) {
          max_score_current_value = Number(this.getCurrentRT.point_cap);
          return max_score_current_value;
        }
        return this.point_scale;
      },
      tags_selected_peer_feed_questions_current: function () {
        const currentQuestions = [];
        this.getCurrentRT.peer_assessment_questions.forEach((point) => {
          if (point.is_active === true) currentQuestions.push(point.question);
        });
        return currentQuestions;
      },
      tags_selected_peer_feed_questions_new: function () {
        const activeQuestions = [];
        this.peer_feedback_questions.forEach((point) => {
          if (point.is_active === true) activeQuestions.push(point.question);
        });
        return activeQuestions;
      },
      tags_selected_points_current: function () {
        const currentTags = [];
        this.getCurrentRT.points_settings.peer_rewards.forEach((point) => {
          currentTags.push(point.tag);
        });
        return currentTags;
      },
      tags_selected_points_new: function () {},
      is_roundtable_end: function () {
        let is_ended = false;
        if (this.getCurrentRT.end_date !== null) {
          is_ended = true;
        }
        return is_ended;
      },
    },
    mixins: [onlineRoundtableSockets, courseSocketMixin],
    methods: {
      openPopper() {
        this.info_open = true;
      },
      closePopper() {
        this.info_open = false;
      },
      setCloseDate: function () {
        if (this.getCurrentRT.end_date !== null) {
          this.close_date.enabled = true;
          this.close_date.date = moment(this.getCurrentRT.end_date).format();
          return;
        }
        this.close_date.date = null;
        return;
      },
      getTimezoneString(date) {
        const d = new Date();
        const dtf = Intl.DateTimeFormat('en-US', { timeZoneName: 'long' });
        const result = dtf
          .formatToParts(d)
          .find((part) => part.type == 'timeZoneName').value;
        return date.toString().split('(')[0] + `(${result})`;
      },
      inputed() {
        if (this.point_scale === '.') {
          this.point_scale = '';
        }
        if (this.point_scale === '' || this.point_scale < 0) {
          this.point_scale = '0';
        }
        if (isNaN(this.point_scale)) {
          this.point_scale = this.point_scale.slice(0, -1);
        } else {
          this.point_scale = this.point_scale.trim();
        }
      },
      arraysAreEqual: function (a, b) {
        if (a === b) return true;
        if (a == null || b == null) return false;
        if (a.length !== b.length) return false;
        for (var i = 0; i < a.length; ++i) {
          if (a[i] !== b[i]) return false;
        }
        return true;
      },
      addPoint() {
        if (this.point_scale === '') {
          this.point_scale = 1;
          return;
        }
        let decimal_places = 0;
        this.point_scale = String(this.point_scale);
        if (this.point_scale.split('.')[1])
          decimal_places = this.point_scale.split('.')[1].length;
        this.point_scale = Number.parseFloat(this.point_scale);
        this.point_scale += 1;
        if (this.point_scale > this.pointCap) {
          this.point_scale = this.pointCap;
        }
        this.point_scale = Number(this.point_scale.toFixed(decimal_places));
      },
      formatPointDescription(point) {
        const { description } = point;
        return Array.isArray(description)
          ? description.join('. ')
          : description;
      },
      createUniqueTag(title, activityType) {
        return `${activityType}${title
          .toLowerCase()
          .trim()
          .replace(/ /g, '_')}${'_'}${Math.random().toString(36).substr(2, 5)}`;
      },
      arePointsExist: function (array1, array2) {
        if (array1.length === array2.length) {
          return array1.every((element) => {
            if (array2.includes(element)) {
              return true;
            }
            return false;
          });
        }
        return false;
      },
      handleValidateCriteriaForm() {
        const toast = { type: 'Error', message: '', time: 5000 };
        if (!this.point_title || (this.point_title && (this.point_title.trim().length < 2 || this.point_title.trim().length > 24))) {
          toast.message = 'Point title needs to be between 1 and 25 characters';
          EventBus.$emit('createToast', toast);
          return;
        }
      },
      handleCreatePoint: async function() {
        this.handleValidateCriteriaForm();
        const { point_title, point_description } = this;
        const prefix = 'all_';
        const new_point = {
          is_custom: true,
          description: point_description,
          points_description: this.points_rubric_description,
          tag: this.createUniqueTag(this.point_title, prefix),
          title: point_title,
          amount: 1,
          category: 'impression',
          icon: 'point.good_analysis.svg',
        };
        const clonedCoursePointSettings = JSON.parse(JSON.stringify(this.getCourseDetail.points_settings.peer_rewards));
        clonedCoursePointSettings.push(new_point);
        await this.$store.dispatch('updateCourseDetail', { id: this.getCourseDetail.id, points_settings: { peer_rewards: clonedCoursePointSettings }});
        this.point_title = null;
        this.point_description = null;
        await this.$store.dispatch('analytics', 'edit written roundtable - create own point type');
        /* Improve this */
        setTimeout(() => {
          this.$emit('editAssessmentPoint', 'online', new_point.tag, this.max_score_enabled ? this.point_scale : null);
        }, 3000);
      },
      handleEditAssessmentPoint(point) {
        const tag = point.tag;
        this.$emit('editAssessmentPoint', 'online', tag, this.max_score_enabled ? this.point_scale : null);
      },
      deleteAssessmentPoint(point) {
        const tag = point.tag;
        this.$emit('deleteAssessmentPoint', tag);
      },
      addPeerQuestion() {
        if (!this.peer_question_new) {
          return;
        }
        const { peer_assessment_questions } = this.getCurrentRT;
        const cloned_peer_questions = JSON.parse(
          JSON.stringify(peer_assessment_questions)
        );
        cloned_peer_questions.push({
          question: this.peer_question_new,
          is_active: true,
          is_custom: true,
        });
        this.$store.dispatch(
          'analytics',
          'edit written roundtable - change peer guidance question'
        );
        this.$store
          .dispatch('savingRTDetails', {
            peer_assessment_questions: cloned_peer_questions,
            id: this.getCurrentRT.id,
          })
          .then(() => {
            this.peer_question_new = '';
            this.peer_feedback_questions = cloned_peer_questions;
          });
      },
      editPeerQuestion(index) {
        this.$emit('editPeerQuestion', index);
      },
      deletePeerQuestion(index) {
        this.$emit('deletePeerQuestion', index);
      },
      saveSettings: async function () {
        if (this.handleChangeSettings === true) {
          const toast = {
            type: 'Warning',
            message: 'Sorry! Nothing to save...',
            time: 5000,
          };
          EventBus.$emit('createToast', toast);
          return;
        }
        if (this.close_date.enabled) {
          this.end_date = new Date(this.close_date.date);
          const timezone_end_date = this.end_date ? new Date(this.end_date) : new Date();
          this.original_timezone_end_date = this.getTimezoneString(timezone_end_date);
        } else {
          this.end_date = null;
          this.original_timezone_end_date = null;
        }

        const payload = {
          id: this.getCurrentRT.id,
          require_submission: this.require_submission,
          enable_annonymous: this.anonymity_status,
          end_date: this.end_date,
          original_timezone_end_date: this.original_timezone_end_date,
          hard_close: this.hard_close_enable,
          enable_grading: this.assessment_status,
          points_settings: this.full_point_settings,
          point_cap: this.max_score_enabled ? this.point_scale : null,
          peer_assessment_questions: this.peer_feedback_questions,
          is_email_notifications: this.is_email_notifications_enable,
          is_genie_feedback_enable: this.is_genie_feedback_enable,
          is_ended: false,
        };

        await this.$store.dispatch('savingRTDetails', payload);

        EventBus.$emit('createToast', {
          type: 'Success',
          message: 'RoundTable settings successfully saved',
          time: 5000,
        });
      },
    },
    created() {
      this.require_submission = this.getCurrentRT.require_submission;
      this.anonymity_status = this.getCurrentRT.enable_annonymous;
      this.hard_close_date = this.getCurrentRT.hard_close;
      this.hard_close_enable = this.getCurrentRT.hard_close;
      this.assessment_status = this.getCurrentRT.enable_grading;
      this.max_score_enabled = this.getCurrentRT.point_cap ? true : false;
      this.point_scale = this.getCurrentRT.point_cap
        ? this.getCurrentRT.point_cap
        : 4;
      this.selected_points = this.getCurrentRT.points_settings.peer_rewards.filter((point) => point).map((point) => point.tag);
      const { peer_assessment_questions } = this.getCurrentRT;
      this.peer_feedback_questions = JSON.parse(
        JSON.stringify(peer_assessment_questions)
      );
      this.setCloseDate();
      this.is_email_notifications_enable =
        this.getCurrentRT.is_email_notifications;
      this.is_genie_feedback_enable =
        this.getCurrentRT.is_genie_feedback_enable;
    },
    mounted() {
      const { courseID, roundtableID } = this.$route.params;
      this.setupCourseSocket(courseID);
      this.leaveRoundtableSocket(roundtableID);
      this.setupRoundtableSocket(roundtableID);
    },
    beforeDestroy() {
      this.leaveRoundtableSocket(this.$route.params.roundtableID);
    },
  });
</script>
<style lang="stylus">
  .custom-border
    border: 1px solid #DBDFEA
  .toggle-button-class
    .v-switch-core
      width: 40px !important
  .toggle-button-class.toggled
    .v-switch-core
      width: 40px !important
      .v-switch-button
        transform: translate3d(21px, 3px, 0px) !important;
        z-index: 0;
</style>
<style lang="scss" scoped>
  input[type='checkbox'] {
    width: 1.25rem !important;
    height: 1.25rem !important;
    -webkit-appearance: none;
    -moz-appearance: none;
    -o-appearance: none;
    appearance: none;
    box-shadow: none;
    font-size: 0.8rem;
    text-align: center;
    line-height: 1.25rem;
    border-radius: 0.25rem;
    --tw-bg-opacity: 1;
    background-color: rgba(243, 244, 246, var(--tw-bg-opacity));
  }
  input[type='checkbox']:checked {
    background-color: #72ccd7;
  }
  input[type='checkbox']:checked::after {
    font-family: 'Font Awesome 5 Free';
    content: '\f00c';
    font-weight: 900;
    color: white;
    line-height: 1.25rem;
  }
</style>



// WEBPACK FOOTER //
// settingsTab.vue?149b13d1